import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import * as A from 'fp-ts/Array';
import { pipe } from 'fp-ts/function';
import * as IOO from 'fp-ts/IOOption';
import * as O from 'fp-ts/Option';

import { Api, SiteCheckpointId } from '@/modules/common/model';
import { BVScheduledFlight } from '@/modules/common/model';
import { String } from '@/utils/effect';

import { arrivalCheckpointChanged, departureCheckpointChanged } from './actions/booking';
import { persistCart } from './actions/cart';

interface UseAutoSetCheckpointsParams {
  departureSite: O.Option<Api.Site>;
  departureCheckpoint: SiteCheckpointId | undefined;
  arrivalCheckpoint: SiteCheckpointId | undefined;
}

export const useAutoSetCheckpoints = ({
  departureSite,
  departureCheckpoint,
  arrivalCheckpoint,
}: UseAutoSetCheckpointsParams) => {
  const dispatch = useDispatch();

  // When departure site has a single checkpoint, auto-select it for both departure and arrival
  useEffect(() => {
    if (!departureCheckpoint) {
      pipe(
        departureSite,
        O.filterMap(({ checkpoints }) => (A.size(checkpoints) === 1 ? A.lookup(0)(checkpoints) : O.none)),
        IOO.fromOption,
        IOO.flatMapIO(checkpoint => () => {
          dispatch(departureCheckpointChanged(checkpoint.id));
          dispatch(arrivalCheckpointChanged(checkpoint.id));
          dispatch(persistCart());
        }),
      )();
    }
  }, [dispatch, departureSite, departureCheckpoint]);

  // When departure checkpoint is set but arrival is not, mirror it to arrival
  useEffect(() => {
    if (departureCheckpoint && !arrivalCheckpoint) {
      dispatch(arrivalCheckpointChanged(departureCheckpoint));
      dispatch(persistCart());
    }
  }, [dispatch, departureCheckpoint, arrivalCheckpoint]);
};

export const useFilteredFlights = <F extends BVScheduledFlight>(flights: Array<F>, search: string) => {
  return useMemo(() => {
    const queryWords = search.toLowerCase().trim().split(/\s+/).filter(String.isNonEmpty);

    if (A.isEmpty(queryWords)) return flights;

    return flights.filter(flight => {
      const searchableText = [
        flight.number,
        flight.carrier.code,
        flight.carrier.name,
        flight.departureAirport.code,
        flight.departureAirport.name,
        flight.departureAirport.city,
        flight.arrivalAirport.code,
        flight.arrivalAirport.name,
        flight.arrivalAirport.city,
      ]
        .join(' ')
        .toLowerCase();

      return queryWords.every(word => searchableText.includes(word));
    });
  }, [flights, search]);
};
